import {naturalSort} from '@benchling/js-utils';
import Chart from 'chart.js';
import moment from 'moment';
import React from 'react';
import _ from 'underscore';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import truncateToMaxLengthLabel from 'ui-library/charting/internals/utils/truncateLabels';
import sortedBy from 'util/sortedBy';

import {getAllXValues, getBaseChartOptions, getDatasetColor} from './BaseChartConfiguration';
import DeprecatedChartJSChart from './DeprecatedChartJSChart';
import {XAxisScale} from './XAxisScale';

export enum Continuous2DChartType {
  LINE = 'line',
}

const X_AXIS_SCALE_TO_CHARTJS_AXIS_TYPE: {[key in XAxisScale]: string} = {
  [XAxisScale.CATEGORICAL]: 'category',
  [XAxisScale.LINEAR]: 'linear',
  [XAxisScale.TIME]: 'time',
};

export type Continuous2DChartData<XType> = Array<{x: XType; y: number}>;

export interface Continuous2DChartDataset<XType> {
  data: Continuous2DChartData<XType>;
  label?: string;
}

export type Continuous2DChartDatasetAxisScale =
  | {datasets: Array<Continuous2DChartDataset<number>>; xAxisScale: XAxisScale.LINEAR}
  | {datasets: Array<Continuous2DChartDataset<string>>; xAxisScale: XAxisScale.TIME}
  | {datasets: Array<Continuous2DChartDataset<string>>; xAxisScale: XAxisScale.CATEGORICAL};

export type Continuous2DChartProps = {
  height?: string | number;
  xAxisLabel: string;
  yAxisLabel: string;
  yAxisIncludesZero: boolean;
} & Continuous2DChartDatasetAxisScale;

export type PossibleXTypes = number | string;

function getTimeScale(xValues: Array<string>): Chart.TimeScale {
  if (xValues.length) {
    const sortedXValues = _.sortBy(xValues);
    const minXValue = sortedXValues[0];
    const maxXValue = sortedXValues[sortedXValues.length - 1];
    const xDuration = moment.duration(moment(maxXValue).diff(minXValue));
    if (xDuration.asDays() > 1 && xDuration.asMonths() < 1) {
      // Usually Chart.js is good at automatically assigning a time unit. But for certain screen widths and
      // ranges < 1 month, it assigns times (12 AM, 12 AM, etc.) even though the expected unit is days (Oct
      // 1, Oct 2, etc.). Manually assign days for this case.
      return {unit: 'day'};
    }
  }
  // Auto-assign a time unit
  return {unit: undefined};
}

function getCategoricalLabels(xValues: Array<string>): Array<string> {
  const labelSet = new Set(xValues);
  return naturalSort(Array.from(labelSet));
}

function datasetToChartJS(
  dataset: Continuous2DChartDataset<PossibleXTypes>,
  datasetIndex: number,
  categoricalXAxis: boolean
): Chart.ChartDataSets {
  return {
    ...getDatasetColor(datasetIndex),
    borderWidth: 2,
    fill: false,
    lineTension: 0,
    ...dataset,
    // Sort operation is for the line chart, which requires sorting to make sure the lines appear as the user
    // expects them (i.e. so the lines are drawn in the right order). Sorting doesn't have a visual effect for
    // the scatter plot since there are no lines.
    data: categoricalXAxis
      ? sortedBy(dataset.data, [
          {
            accessor: 'x',
            naturalSort: true,
          },
          {
            accessor: 'y',
            naturalSort: false, // naturalSort not necessary for y since it's numeric
          },
        ])
      : sortedBy(dataset.data, ['x', 'y']),
  };
}

// By default, Chart.js's categorical x labels for continuous 2-D chart are wrong for datasets whose x-axis
// values don't match the x-axis's labels in the same order: https://github.com/chartjs/Chart.js/issues/5874
// (repetitions or omissions).
//
// For example, let's say the x-axis's labels are ["B0", "B1"].
// Suppose our dataset's x values are ["B0", "B0", "B1", "B1"] (samples measured in duplicate). We want those
// to be our x labels within the tooltip. But Chart.js displays ["B0", "B1", undefined, undefined] by default.
// Now suppose our dataset's x values are ["B1"] (sparse - skipped B0). We want that to be our x label within
// the tooltip. But Chart.js displays ["B0"] by default.
//
// To fix this, we directly look up the point's x value from the dataset.
function getTooltipLabel(tooltipItem: Chart.ChartTooltipItem, tooltipData: Chart.ChartData): string {
  const point = tooltipData.datasets![tooltipItem.datasetIndex!].data![
    tooltipItem.index!
  ] as Chart.ChartPoint;
  // tooltipItem.label is the x label, and it's incorrect (see comment above). So, look up the point's x
  // value instead.
  const xLabel = String(point.x);
  // tooltipItem.value is the y label, and it's correct
  const yLabel = tooltipItem.value!;
  return `(${xLabel}, ${yLabel})`;
}

// Same bug as above. Since the tooltip label already displays both (x, y), don't render the tooltip title (by
// returning []).
function getTooltipTitle(
  tooltipItems: Array<Chart.ChartTooltipItem>,
  tooltipData: Chart.ChartData
): Array<string> {
  return [];
}

/**
 * Abstract component for continuous 2-D charts (e.g., line chart or scatter plot)
 *
 * This component handles line charts (and formerly scatter plots).
 */
export default function Continuous2DChart({
  chartType,
  chartProps,
  dataTestAttrs,
}: {
  chartType: Continuous2DChartType;
  chartProps: Continuous2DChartProps;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  return (
    <DeprecatedChartJSChart
      chartConfiguration={{
        type: chartType,
        data: {
          ...(chartProps.xAxisScale === XAxisScale.CATEGORICAL
            ? {labels: getCategoricalLabels(getAllXValues(chartProps.datasets))}
            : {}),
          datasets: (chartProps.datasets as Array<Continuous2DChartDataset<PossibleXTypes>>).map(
            (dataset, datasetIndex) =>
              datasetToChartJS(dataset, datasetIndex, chartProps.xAxisScale === XAxisScale.CATEGORICAL)
          ),
        },
        options: {
          ...getBaseChartOptions(chartProps.datasets),
          scales: {
            xAxes: [
              {
                display: true,
                scaleLabel: {
                  display: true,
                  labelString: chartProps.xAxisLabel,
                },
                time:
                  chartProps.xAxisScale === XAxisScale.TIME
                    ? getTimeScale(getAllXValues(chartProps.datasets))
                    : undefined,
                type: X_AXIS_SCALE_TO_CHARTJS_AXIS_TYPE[chartProps.xAxisScale],
                ticks: {
                  callback: (value) => truncateToMaxLengthLabel(value.toString()),
                },
              },
            ],
            yAxes: [
              {
                display: true,
                scaleLabel: {
                  display: true,
                  labelString: chartProps.yAxisLabel,
                },
                ticks: {beginAtZero: chartProps.yAxisIncludesZero},
                type: 'linear',
              },
            ],
          },
          tooltips: {callbacks: {label: getTooltipLabel, title: getTooltipTitle}},
        },
      }}
      height={chartProps.height}
      dataTestAttrs={dataTestAttrs}
    />
  );
}
